import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { SalesPaymentStage, SalesReportRow } from "@/core";
import { useSalesOrderReport } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  differenceInCalendarDays,
  endOfMonth,
  endOfYear,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function formatNumber(value: number): string {
  return Math.round(value).toLocaleString();
}

function formatPct(ratio: number | null): string {
  if (ratio === null || !Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatDays(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value)}d`;
}

function toDateKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

// Margin / collected reconcile against different bases on purpose:
//  - margin % is over pre-tax miles revenue (tax is pass-through, not profit)
//  - collected / outstanding are over booked total (revenue + tax = what's billed)
interface Summary {
  count: number;
  bookedRevenue: number; // Σ totalAmount
  milesRevenue: number; // Σ revenue (pre-tax) — margin base
  totalCost: number; // Σ costBasis
  totalMargin: number; // Σ margin
  marginPct: number | null; // totalMargin / milesRevenue
  aov: number; // bookedRevenue / count
  avgDaysToSell: number | null;
  collected: number;
  outstanding: number;
}

function summarize(rows: SalesReportRow[]): Summary {
  const count = rows.length;
  const bookedRevenue = sum(rows, (r) => r.totalAmount);
  const milesRevenue = sum(rows, (r) => r.revenue);
  const totalCost = sum(rows, (r) => r.costBasis);
  const totalMargin = sum(rows, (r) => r.margin);
  const collected = sum(rows, (r) => r.amountCollected);
  const outstanding = sum(rows, (r) => r.amountOutstanding);

  const sellable = rows.filter((r) => r.daysToSell !== null);
  const avgDaysToSell = sellable.length
    ? sum(sellable, (r) => r.daysToSell ?? 0) / sellable.length
    : null;

  return {
    count,
    bookedRevenue,
    milesRevenue,
    totalCost,
    totalMargin,
    marginPct: milesRevenue > 0 ? totalMargin / milesRevenue : null,
    aov: count > 0 ? bookedRevenue / count : 0,
    avgDaysToSell,
    collected,
    outstanding,
  };
}

function sum<T>(items: T[], selector: (item: T) => number): number {
  return items.reduce((total, item) => total + selector(item), 0);
}

function pctChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return (current - previous) / previous;
}

const STAGE_META: { key: SalesPaymentStage; label: string }[] = [
  { key: "paid", label: "Paid in full" },
  { key: "partial", label: "Deposit / partial" },
  { key: "unpaid", label: "Unpaid" },
];

interface GroupRow {
  key: string;
  label: string;
  count: number;
  bookedRevenue: number;
  milesRevenue: number;
  margin: number;
  marginPct: number | null;
  avgDaysToSell: number | null;
}

function groupBy(
  rows: SalesReportRow[],
  keyOf: (r: SalesReportRow) => string,
  labelOf: (r: SalesReportRow) => string,
): GroupRow[] {
  const buckets = new Map<string, SalesReportRow[]>();
  for (const r of rows) {
    const key = keyOf(r);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(r);
    else buckets.set(key, [r]);
  }

  return [...buckets.entries()]
    .map(([key, bucket]) => {
      const milesRevenue = sum(bucket, (r) => r.revenue);
      const margin = sum(bucket, (r) => r.margin);
      const sellable = bucket.filter((r) => r.daysToSell !== null);
      return {
        key,
        label: labelOf(bucket[0]),
        count: bucket.length,
        bookedRevenue: sum(bucket, (r) => r.totalAmount),
        milesRevenue,
        margin,
        marginPct: milesRevenue > 0 ? margin / milesRevenue : null,
        avgDaysToSell: sellable.length
          ? sum(sellable, (r) => r.daysToSell ?? 0) / sellable.length
          : null,
      };
    })
    .sort((a, b) => b.bookedRevenue - a.bookedRevenue);
}

function Delta({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-xs text-muted-foreground">no prior data</span>;
  }
  const up = value >= 0;
  const magnitude = Math.abs(value * 100);
  // A tiny / near-empty prior period makes the % explode (e.g. 2 → 370 sales =
  // +18,400%), which is noise, not signal. Cap the display so it reads as
  // "grew enormously" instead of a spurious precise number.
  const label = magnitude > 999 ? ">999" : magnitude.toFixed(1);
  return (
    <span
      className={cn(
        "text-xs font-medium",
        up ? "text-emerald-600" : "text-red-600",
      )}
    >
      {up ? "▲" : "▼"} {label}% vs prev
    </span>
  );
}

export default function SalesReportPage() {
  const now = useMemo(() => new Date(), []);
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(now),
    to: endOfMonth(now),
  });

  const startDate = range?.from ? toDateKey(range.from) : "";
  const endDate = toDateKey(range?.to ?? range?.from ?? now);

  // Comparison window: the previous calendar month when a whole month is
  // selected (the common "this vs last" case), otherwise the equal-length
  // window immediately preceding the selection.
  const prev = useMemo(() => {
    if (!range?.from || !range?.to) return { startDate: "", endDate: "" };
    const isFullMonth =
      isSameDay(range.from, startOfMonth(range.from)) &&
      isSameDay(range.to, endOfMonth(range.from)) &&
      isSameMonth(range.from, range.to);
    if (isFullMonth) {
      const prevMonth = subMonths(range.from, 1);
      return {
        startDate: toDateKey(startOfMonth(prevMonth)),
        endDate: toDateKey(endOfMonth(prevMonth)),
      };
    }
    const lengthDays = differenceInCalendarDays(range.to, range.from);
    const prevTo = subDays(range.from, 1);
    const prevFrom = subDays(prevTo, lengthDays);
    return { startDate: toDateKey(prevFrom), endDate: toDateKey(prevTo) };
  }, [range]);

  const { data, isLoading, isError } = useSalesOrderReport({
    startDate,
    endDate,
  });
  const { data: prevData } = useSalesOrderReport(prev);

  const rows = useMemo(() => data ?? [], [data]);
  const summary = useMemo(() => summarize(rows), [rows]);
  const prevSummary = useMemo(() => summarize(prevData ?? []), [prevData]);

  const byProduct = useMemo(
    () =>
      groupBy(
        rows,
        (r) => r.productId ?? r.productName ?? "unknown",
        (r) => r.productName ?? "Unknown product",
      ),
    [rows],
  );
  const byCategory = useMemo(
    () =>
      groupBy(
        rows,
        (r) => r.category ?? "uncategorized",
        (r) => r.category ?? "Uncategorized",
      ),
    [rows],
  );

  const byStage = useMemo(() => {
    return STAGE_META.map((stage) => {
      const bucket = rows.filter((r) => r.paymentStage === stage.key);
      return {
        ...stage,
        count: bucket.length,
        bookedRevenue: sum(bucket, (r) => r.totalAmount),
        collected: sum(bucket, (r) => r.amountCollected),
        outstanding: sum(bucket, (r) => r.amountOutstanding),
      };
    });
  }, [rows]);

  const setPreset = (preset: "this" | "last" | "last3" | "ytd") => {
    if (preset === "this") {
      setRange({ from: startOfMonth(now), to: endOfMonth(now) });
    } else if (preset === "last") {
      const prevMonth = subMonths(now, 1);
      setRange({ from: startOfMonth(prevMonth), to: endOfMonth(prevMonth) });
    } else if (preset === "last3") {
      setRange({ from: startOfMonth(subMonths(now, 2)), to: endOfMonth(now) });
    } else {
      setRange({ from: startOfYear(now), to: endOfYear(now) });
    }
  };

  const rangeLabel =
    range?.from && range?.to
      ? `${format(range.from, "PP")} – ${format(range.to, "PP")}`
      : range?.from
        ? format(range.from, "PP")
        : "Pick a range";

  return (
    <div className="container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales</h1>
          <p className="text-sm text-muted-foreground">
            Sales for the selected period with gross margin against the linked
            purchase-order cost. Cancelled sales are excluded. Margin = miles
            revenue − stock cost (tax excluded); collected vs outstanding is over
            the booked total (incl. tax). Uses the same date logic as the
            Purchase Orders report.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPreset("this")}>
              This month
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("last")}>
              Last month
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPreset("last3")}
            >
              Last 3 months
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("ytd")}>
              YTD
            </Button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[260px] justify-start text-left font-normal",
                  !range?.from && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={2}
                defaultMonth={range?.from}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {isError ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Couldn't load the sales report. Try again or adjust the range.
        </p>
      ) : isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Loading sales report…
        </p>
      ) : (
        <>
          {/* Headline KPIs with period-over-period deltas */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard
              label="Sales"
              value={formatNumber(summary.count)}
              caption="Orders in range (excl. drafts and cancelled)"
              delta={pctChange(summary.count, prevSummary.count)}
            />
            <KpiCard
              label="Booked Revenue"
              value={formatCurrency(summary.bookedRevenue)}
              caption="Booked total (revenue + tax)"
              delta={pctChange(summary.bookedRevenue, prevSummary.bookedRevenue)}
            />
            <KpiCard
              label="Gross Margin"
              value={formatCurrency(summary.totalMargin)}
              caption={`Avg margin ${formatPct(summary.marginPct)} · cost ${formatCurrency(
                summary.totalCost,
              )}`}
              delta={pctChange(summary.totalMargin, prevSummary.totalMargin)}
            />
            <KpiCard
              label="Avg Order Value"
              value={formatCurrency(summary.aov)}
              caption="Booked total ÷ sales"
              delta={pctChange(summary.aov, prevSummary.aov)}
            />
            <KpiCard
              label="Avg Days to Sell"
              value={formatDays(summary.avgDaysToSell)}
              caption="Sale date − earliest PO date"
              delta={
                summary.avgDaysToSell !== null &&
                prevSummary.avgDaysToSell !== null
                  ? pctChange(summary.avgDaysToSell, prevSummary.avgDaysToSell)
                  : null
              }
            />
            <KpiCard
              label="Collected / Outstanding"
              value={formatCurrency(summary.collected)}
              caption={`${formatCurrency(summary.outstanding)} still owed`}
              delta={pctChange(summary.collected, prevSummary.collected)}
            />
          </div>

          {/* By payment stage */}
          <Card>
            <CardHeader>
              <CardTitle>By Payment Stage</CardTitle>
              <CardDescription>
                How much booked revenue has been collected vs is still
                outstanding, by where each sale's invoices stand.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table containerClassName="overflow-auto">
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Sales</TableHead>
                    <TableHead className="text-right">Booked</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byStage.map((stage) => (
                    <TableRow key={stage.key}>
                      <TableCell className="font-medium">{stage.label}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(stage.count)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(stage.bookedRevenue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(stage.collected)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(stage.outstanding)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatNumber(summary.count)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(summary.bookedRevenue)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(summary.collected)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(summary.outstanding)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>

          {/* By product + by category */}
          <div className="grid gap-6 lg:grid-cols-2">
            <BreakdownCard
              title="By Product"
              description="Pre-tax revenue and margin per product, best sellers first."
              rows={byProduct}
            />
            <BreakdownCard
              title="By Category"
              description="Pre-tax revenue and margin per category."
              rows={byCategory}
            />
          </div>

          {/* Per-sale detail */}
          <Card>
            <CardHeader>
              <CardTitle>Sales ({formatNumber(rows.length)})</CardTitle>
              <CardDescription>
                Every sale in range, traced to its stock cost and purchase-order
                date.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table containerClassName="max-h-[600px] overflow-auto">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Miles</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-right">Margin %</TableHead>
                    <TableHead className="whitespace-nowrap">PO date</TableHead>
                    <TableHead className="text-right">Days to sell</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        className="py-12 text-center text-sm text-muted-foreground"
                      >
                        No sales in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">
                          {r.saleDate}
                        </TableCell>
                        <TableCell>{r.productName ?? "—"}</TableCell>
                        <TableCell>{r.category ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(r.totalMilesSold)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(r.revenue)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(r.costBasis)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            r.margin < 0 && "text-red-600",
                          )}
                        >
                          {formatCurrency(r.margin)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatPct(r.marginPct)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {r.poDate ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDays(r.daysToSell)}
                        </TableCell>
                        <TableCell>
                          {STAGE_META.find((s) => s.key === r.paymentStage)
                            ?.label ?? r.paymentStage}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(r.amountOutstanding)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  caption,
  delta,
}: {
  label: string;
  value: string;
  caption: string;
  delta: number | null;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        <Delta value={delta} />
        <span className="text-sm text-muted-foreground">{caption}</span>
      </CardContent>
    </Card>
  );
}

function BreakdownCard({
  title,
  description,
  rows,
}: {
  title: string;
  description: string;
  rows: GroupRow[];
}) {
  // Revenue shown here is pre-tax miles revenue so that Margin ÷ Revenue equals
  // the Margin % column (margin is also pre-tax). The headline "Booked Revenue"
  // KPI is the booked total incl. tax — a deliberately different figure.
  const totalRevenue = sum(rows, (r) => r.milesRevenue);
  const totalMargin = sum(rows, (r) => r.margin);
  const totalMilesRevenue = sum(rows, (r) => r.milesRevenue);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table containerClassName="max-h-[420px] overflow-auto">
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead>{title.replace("By ", "")}</TableHead>
              <TableHead className="text-right">Sales</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">Margin</TableHead>
              <TableHead className="text-right">Margin %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  No sales in this range.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell className="text-right">
                    {formatNumber(r.count)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(r.milesRevenue)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right",
                      r.margin < 0 && "text-red-600",
                    )}
                  >
                    {formatCurrency(r.margin)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPct(r.marginPct)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">Total</TableCell>
                <TableCell className="text-right font-semibold">
                  {formatNumber(sum(rows, (r) => r.count))}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(totalRevenue)}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(totalMargin)}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatPct(
                    totalMilesRevenue > 0 ? totalMargin / totalMilesRevenue : null,
                  )}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </CardContent>
    </Card>
  );
}
